import { createSlice, original } from '@reduxjs/toolkit';

import { spread } from '@/utils/spread';
import { buildReducerError, getDiffProducts, restoreInitialState } from '@/utils/reducerUtils';
import { SlicePayload } from '@/types';
import { BoxModel } from '@/models';

type BoxStateStatusValue = { success: true, redirectUrl?: string } | { error: any } | Record<string, never>;
type BoxStateStatusKey = 'get' | 'add' | 'delete' | 'change';

type BoxStateStatus = {
  showConfirm?: boolean;
  firstElem?: boolean;
} & { [key in BoxStateStatusKey]?: BoxStateStatusValue };

export type BoxState = {
  content: BoxModel;
  resetBoxQty: boolean;
  last: any[];
  diff: any[];
  status: BoxStateStatus;
  showAddedProduct: boolean;
  wineBoxPush?: any;
}

export const initialState: BoxState = {
  content: {
    wineBox: true,
    products: []
  } as unknown as BoxModel,
  resetBoxQty: false,
  last: [],
  diff: [],
  status: {},
  showAddedProduct: false,
  wineBoxPush: null
};

const box = createSlice({
  name: 'box',
  initialState,
  reducers: {
    // RESET
    resetBox: (state) => {
      restoreInitialState(state, initialState);
    },

    // GET
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getBox: () => {
    },

    getBoxSuccess: (state, action: SlicePayload<{ content: BoxModel }>) => {
      state.last = spread(action.payload.content.products);
      state.content = action.payload.content;
      state.status.get = { success: true };
    },

    getBoxError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.get = buildReducerError(action.payload);
    },

    getBoxClear: (state) => {
      delete state.status.get;
    },

    resetBoxQty: (state) => {
      state.resetBoxQty = !state.resetBoxQty;
    },

    // ADD
    addBoxProduct: (
      _state,
      _action: SlicePayload<{
        item: string, qty: number, options?: { set?: boolean; showConfirm?: boolean }
      }>
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    ) => {
    },

    addBoxProductSuccess: (state, action: SlicePayload<{ content: BoxModel, firstElem: boolean, showConfirm?: boolean }>) => {
      const { content, showConfirm = false, firstElem = false } = action.payload;
      state.diff = getDiffProducts(content.products, original(state.last));
      state.last = spread(content.products);
      state.status.add = { success: true };
      state.content = content;
      state.status.showConfirm = showConfirm;
      state.status.firstElem = firstElem;
    },

    addBoxProductError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.add = buildReducerError(action.payload);
    },

    addBoxProductClear: (state) => {
      delete state.status.add;
    },

    // SHOW/HIDE
    showAddBoxProductSuccess: (state) => {
      state.showAddedProduct = true;
    },

    hideAddBoxProductSuccess: (state) => {
      state.showAddedProduct = false;
    },

    // DELETE
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    deleteBox: (_state, _action: SlicePayload<{ wineBoxPush?: boolean, t: (_k: string) => string }>) => {
    },

    deleteBoxSuccess: (state) => {
      restoreInitialState(state, initialState);
    },

    deleteBoxError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.delete = buildReducerError(action.payload);
    },

    deleteBoxClear: (state) => {
      delete state.status.delete;
    },

    // WINEBOX
    setWineBoxPush: (state, action: SlicePayload<{ wineBoxPush: any }>) => {
      state.wineBoxPush = action.payload.wineBoxPush;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    createWineBoxFromPush: (_state, _action: SlicePayload<{ itemsList: any[], t: (_k: string) => string }>) => {
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    replaceCurrentWithWineBoxFromPush: (_state, _action: SlicePayload<{ itemsList: any[], t: (_k: string) => string }>) => {
    }
  }
});

export const {
  resetBox,
  getBox, getBoxSuccess, getBoxError, getBoxClear, resetBoxQty,
  addBoxProduct, addBoxProductSuccess, addBoxProductError, addBoxProductClear,
  showAddBoxProductSuccess, hideAddBoxProductSuccess,
  deleteBox, deleteBoxSuccess, deleteBoxError, deleteBoxClear,
  setWineBoxPush, createWineBoxFromPush, replaceCurrentWithWineBoxFromPush
} = box.actions;

export default box.reducer;
